import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["query"]
  static values = { delay: { type: Number, default: 300 } }

  connect() {
    document.addEventListener("turbo:before-cache", this.captureSelection, true)
    document.addEventListener("turbo:before-fetch-request", this.handleFetchRequest)
    document.addEventListener("turbo:before-fetch-response", this.allowSubmittedVisit)
    document.addEventListener("turbo:before-visit", this.cancelUnrelatedVisit)
    document.addEventListener("turbo:before-render", this.prepareForRender)
    document.addEventListener("turbo:click", this.cancelPreservation)
    window.addEventListener("popstate", this.cancelPreservation)
    if (this.hasQueryTarget) this.queryTarget.removeAttribute("data-turbo-permanent")
  }

  schedule() {
    this.captureLatestQuery()
    if (this.submittedQuery !== undefined) this.preserveQuery()
    window.clearTimeout(this.timeout)
    this.timeout = window.setTimeout(() => this.submitForm(), this.delayValue)
  }

  submit() {
    window.clearTimeout(this.timeout)
    this.submitForm()
  }

  submitForm() {
    this.captureLatestQuery()
    this.submittedQuery = this.latestQuery
    this.preserveQuery()
    this.element.requestSubmit()
  }

  preserveQuery() {
    if (this.queryTarget === document.activeElement) {
      this.queryTarget.setAttribute("data-turbo-permanent", "")
    }
  }

  captureLatestQuery() {
    this.latestQuery = this.queryTarget.value
    this.captureSelection()
  }

  captureSelection = () => {
    if (this.hasQueryTarget && this.queryTarget === document.activeElement) {
      this.selection = {
        start: this.queryTarget.selectionStart,
        end: this.queryTarget.selectionEnd,
        direction: this.queryTarget.selectionDirection
      }
    }
  }

  handleFetchRequest = (event) => {
    if (event.target === this.element || !event.target.hasAttribute("data-turbo-stream")) return
    if (this.submittedQuery === undefined) return

    this.submittedQuery = undefined
    this.schedule()
  }

  allowSubmittedVisit = (event) => {
    if (event.target === this.element && event.detail.fetchResponse.succeeded) {
      this.submittedVisitPending = true
    }
  }

  cancelUnrelatedVisit = () => {
    if (this.submittedVisitPending) {
      this.submittedVisitPending = false
      return
    }

    this.cancelPreservation()
  }

  cancelPreservation = () => {
    window.clearTimeout(this.timeout)
    this.submittedVisitPending = false
    this.submittedQuery = undefined
    this.latestQuery = undefined
    this.selection = undefined
    if (this.hasQueryTarget) this.queryTarget.removeAttribute("data-turbo-permanent")
  }

  prepareForRender = (event) => {
    if (this.submittedQuery === undefined) {
      this.queryTarget.blur()
      document.addEventListener("turbo:render", this.restoreRenderedQuery, { once: true })
      return
    }

    const renderedQuery = event.detail.newBody.querySelector('[data-booking-search-target~="query"]')
    this.queryTarget.value = this.latestQuery
    if (this.selection) {
      this.queryTarget.setSelectionRange(
        this.selection.start,
        this.selection.end,
        this.selection.direction
      )
    }

    const shouldResubmit = this.latestQuery !== this.submittedQuery
    const renderedDefaultValue = renderedQuery?.defaultValue
    document.addEventListener(
      "turbo:render",
      () => this.finishRender(shouldResubmit, renderedDefaultValue),
      { once: true }
    )
  }

  restoreRenderedQuery = () => {
    const query = document.querySelector('[data-booking-search-target~="query"]')
    if (query) query.value = query.defaultValue
  }

  finishRender(shouldResubmit, renderedDefaultValue) {
    const query = document.querySelector('[data-booking-search-target~="query"]')
    if (!query) return

    query.removeAttribute("data-turbo-permanent")
    if (renderedDefaultValue !== undefined) query.defaultValue = renderedDefaultValue

    if (shouldResubmit) {
      window.requestAnimationFrame(() => {
        if (query.isConnected) query.dispatchEvent(new Event("input", { bubbles: true }))
      })
    }
  }

  disconnect() {
    window.clearTimeout(this.timeout)
    document.removeEventListener("turbo:before-cache", this.captureSelection, true)
    document.removeEventListener("turbo:before-fetch-request", this.handleFetchRequest)
    document.removeEventListener("turbo:before-fetch-response", this.allowSubmittedVisit)
    document.removeEventListener("turbo:before-visit", this.cancelUnrelatedVisit)
    document.removeEventListener("turbo:before-render", this.prepareForRender)
    document.removeEventListener("turbo:click", this.cancelPreservation)
    window.removeEventListener("popstate", this.cancelPreservation)
  }
}
